import { EdgeConfig, NodeConfig } from '@antv/g6-core/lib/types';
import Color from 'color';

import { NodePosition } from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { DatumTypeTokenToGraphProperties } from 'dashboard/models/graph/node-render-config';
import {
  ServerColorRule,
  ServerNodeDatumType,
  ServerNodeFunctionsConfig,
  ServerNodeRenderConfig,
  ServerPresetSummary,
  ServerPropertyConfig,
  ServerSearchableField,
} from 'dashboard/models/graph-backend';
import { LightlyHydratedNode } from 'dashboard/models/graph-hydration';
import { InquiryStatus } from 'dashboard/models/inquiry';
import { PropertyVertexType, VertexType } from 'lib/constants/graph';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

export * from 'dashboard/models/graph/node-render-config';

export type Tag = string;

export interface GraphNodeConfigAttributes {
  'datum-type-token': string;
  'node-render-config': ServerNodeRenderConfig;
  'node-functions-config': ServerNodeFunctionsConfig;
}

export interface GraphAttributes {
  'min-cluster-size': number;
  'max-cluster-size': number;
  'property-filters': PropertyVertexType[];
  tags: string[];
  'available-tags': Tag[];
  'date-range': number;
  'coloring-rules': ServerColorRule[];
  'default-graph-properties': DatumTypeTokenToGraphProperties;
  'node-functions-configs': ServerNodeFunctionsConfig[];
  'searchable-fields': ServerSearchableField[];
  'preset-summaries': ServerPresetSummary[];
  'node-render-configs': ServerNodeRenderConfig[];
  'node-types': ServerNodeDatumType[];
  'property-configs': ServerPropertyConfig[];
  'demo-backend-name'?: string;
  'demo-backend-payload'?: string;
}

export interface GraphRelationships {
  organization: ApiRelationship<ApiModel.Organization>;
  environment: ApiRelationship<ApiModel.Environment>;
}

export type Graph = ApiResource<GraphAttributes, GraphRelationships, ApiModel.Graph>;

export type GraphNodeId = string;

export interface GraphAccountNode {
  type: VertexType.Account;
  id: GraphNodeId;
  'name-full'?: string;
  'name-initials'?: string;
  tags: string[];
}

export interface GraphPropertyNode {
  type: PropertyVertexType;
  id: GraphNodeId;
  value?: string;
}

export type GraphNode = GraphAccountNode | GraphPropertyNode;

export interface GraphLink {
  source: GraphNodeId;
  target: GraphNodeId;
}

export interface GraphTextResultCluster {
  'property-id': string;
  'property-type': PropertyVertexType;
  'property-value': string;
  accounts: GraphTextResultItem[];
}

export interface GraphInquiries {
  status: InquiryStatus;
  count: number;
}

export interface GraphTextResultItem {
  token: string;
  'reference-id': string;
  'name-full'?: string;
  tags: string[];
  inquiries: GraphInquiries[];
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
  clusters?: GraphTextResultCluster[];
  'is-truncated'?: boolean;
  timestamp?: string;
}

export interface GraphExportCluster {
  'property-id': string;
  'property-type': PropertyVertexType;
  accounts: string[];
}

export interface ColorRuleStatus {
  type: 'status';
  status: string;
  color: Color;
}

export interface ColorRuleTag {
  type: 'tag';
  tag: string;
  color: Color;
}

export interface ColorRuleCondition {
  type: 'condition';
  condition: string;
  color: Color;
}

export enum NodeConditions {
  SeedSet = 'seed set',
  NoCompletedInquiries = 'no completed inquiries',
}

export type ColorRule = ColorRuleStatus | ColorRuleTag | ColorRuleCondition;

// --------------------------------------------------------------------------
// Graph V2 types
// --------------------------------------------------------------------------

export type GraphId = string;
export type DatumTypeToken = string;
export type DatumTypeValue = string;

export enum DatumValueType {
  // Basic value types.
  String = 'string',
  Integer = 'int',
  Decimal = 'decimal',
  Boolean = 'boolean',

  // More precise types.
  Account = 'account',
  AccountSimilaritySelfie = 'account_similarity_selfie',
  Address = 'address',
  Date = 'date',
  Email = 'email',
  EmailSimilarity = 'email_similarity',
  ImageBackgroundSimilarity = 'image_background_similarity',
  GovernmentId = 'government_id',
  IdDocumentNumber = 'id_document_number',
  IpAddress = 'ip_address',
  Name = 'name',
  PhoneNumber = 'phone',
  PostalCode = 'postal_code',
  SocialSecurityNumber = 'ssn',
  SocialSecurityNumberLast4 = 'ssn_last4',
  UUID = 'uuid',

  // Transaction demo types.
  ExternalAccount = 'external_account',
  Transactions = 'transactions',
}

export enum PersonaPropertyValues {
  Status = 'status',
  Tags = 'tags',
  NumberOfInquiries = 'number_of_inquiries',
  InquiryBreakdown = 'inquiry_breakdown',
  ReferenceId = 'reference_id',
  CreatedAt = 'created_at',
  Name = 'name',
  // network properties
  ProxyType = 'proxy_type',
  ThreatLevel = 'threat_level',
  Location = 'location',
  // device fingerprint properties
  DeviceType = 'device_type',
  // browser fingerprint properties
  BrowserName = 'browser_name',
}

export enum GraphPropertyType {
  // Basic value types.
  String = 'string',
  Integer = 'int',
  Decimal = 'decimal',
  Boolean = 'boolean',

  // More precise types.
}

interface GraphNodeAttributes {}

interface GraphNodeRelationships {}

export type ServerGraphNode = ApiResource<
  GraphNodeAttributes,
  GraphNodeRelationships,
  ApiModel.GraphNode
>;

export interface DatumType {
  token: DatumTypeToken;
  isCustomType: boolean;
  name: string;
  valueType: DatumValueType;
}

export interface NodeId {
  token: DatumTypeToken;
  // The value is normalized, so if two nodes share the same datum type and value, they're the same node.
  value: DatumTypeValue;
}

// Declared here, because JS/TS does referential comparisons for hash objects (eg. Set and Map), instead of comparing tuples by values.
declare const HashableNodeIdSymbol: unique symbol;
export type HashableNodeId = string & { _opaque: typeof HashableNodeIdSymbol };

// Putting these conversion functions here, since other places are probably worse.
export const nodeIdToHashable = (nodeId: NodeId): HashableNodeId => {
  return `${nodeId.token}:${nodeId.value}` as HashableNodeId;
};

export const nodeIdFromHashable = (hashableNodeId: HashableNodeId): NodeId => {
  if (hashableNodeId == null) {
    throw new Error('hashableNodeId is null');
  }
  const colonIndex = hashableNodeId.indexOf(':');
  const [token, value] = [
    hashableNodeId.slice(0, colonIndex),
    hashableNodeId.slice(colonIndex + 1),
  ];
  return {
    token,
    value,
  };
};

// Declared here, because JS/TS does referential comparisons for hash objects (eg. Set and Map), instead of comparing tuples by values.
declare const HashableNodeIdPairSymbol: unique symbol;
export type HashableNodeIdPair = string & { _opaque: typeof HashableNodeIdPairSymbol };

export const hashableNodeIdPairToHashable = (nodeId1: HashableNodeId, nodeId2: HashableNodeId) => {
  return JSON.stringify([nodeId1, nodeId2].sort()) as HashableNodeIdPair;
};

export const nodeIdPairToHashable = (nodeId1: NodeId, nodeId2: NodeId): HashableNodeIdPair => {
  return hashableNodeIdPairToHashable(nodeIdToHashable(nodeId1), nodeIdToHashable(nodeId2));
};

export interface NodeState {
  visible: boolean;
  selected: boolean;
  highlighted: boolean;
  dimmed: boolean;
  loading: boolean;
  new: boolean;
  nonfetchedEdgeCount: number;
  excluded: boolean;
  // Used in case cards to showcase accounts whose ids are found in the query
  featured: boolean;
}

export interface PropertyConfig {
  token: DatumTypeToken;
  displayName: string;
  indexed: boolean;
  source: string;
  value: string;
  type: GraphPropertyType;
}

// Note: We'll refactor the node interface to be minimal, and move all of the account-specific fields to a hydrated data structure.
export interface Node {
  id: NodeId;
  state: NodeState;
  hoverCards: Set<HTMLDivElement>;
}

export type CollapsedGroupId = string;

export type CollapsedPairwiseNodeId = string;

export interface CollapsedGroup {
  groupId: CollapsedGroupId;
  members: Set<HashableNodeId>;
  state: NodeState;
  relativeNodePositions: Map<HashableNodeId, NodePosition>;
  hoverCards: Set<HTMLDivElement>;
}

export interface CollapsedPairwiseNode {
  id: NodeId;
  members: Set<HashableNodeId>;
  state: NodeState;
  hoverCards: Set<HTMLDivElement>;
}

export enum RenderedCanvasNodeType {
  Node = 'node',
  CollapsedGroup = 'collapsed_group',
  CollapsedPairwiseNode = 'collapsed_pairwise_node',
}

export interface CanvasNodeSelectable {
  type: RenderedCanvasNodeType.Node;
  selectable: NodeId;
}

export interface CanvasCollapsedGroupSelectable {
  type: RenderedCanvasNodeType.CollapsedGroup;
  selectable: CollapsedGroupId;
}

export interface CanvasCollapsedPairwiseNodeSelectable {
  type: RenderedCanvasNodeType.CollapsedPairwiseNode;
  selectable: CollapsedPairwiseNodeId;
}

export type CanvasSelectable =
  | CanvasNodeSelectable
  | CanvasCollapsedGroupSelectable
  | CanvasCollapsedPairwiseNodeSelectable;

export type HashableCanvasSelectable = string;

export const nodeIdToSelectable = (nodeId: NodeId): CanvasNodeSelectable => {
  return {
    type: RenderedCanvasNodeType.Node,
    selectable: nodeId,
  };
};

export const collapsedPairwiseNodeIdToSelectable = (
  CcollapsedPairwiseNodeId: CollapsedPairwiseNodeId,
): CanvasCollapsedPairwiseNodeSelectable => {
  return {
    type: RenderedCanvasNodeType.CollapsedPairwiseNode,
    selectable: CcollapsedPairwiseNodeId,
  };
};

export const collapsedGroupIdToSelectable = (
  collapsedGroupId: CollapsedGroupId,
): CanvasCollapsedGroupSelectable => {
  return {
    type: RenderedCanvasNodeType.CollapsedGroup,
    selectable: collapsedGroupId,
  };
};

export const selectableToHashable = (
  canvasSelectable: CanvasSelectable,
): HashableCanvasSelectable => {
  switch (canvasSelectable.type) {
    case RenderedCanvasNodeType.Node:
      return `${RenderedCanvasNodeType.Node}|${nodeIdToHashable(canvasSelectable.selectable)}`;
    case RenderedCanvasNodeType.CollapsedPairwiseNode:
      return `${RenderedCanvasNodeType.CollapsedPairwiseNode}|${canvasSelectable.selectable}`;
    case RenderedCanvasNodeType.CollapsedGroup:
      return `${RenderedCanvasNodeType.CollapsedGroup}|${canvasSelectable.selectable}`;
  }
};

export const selectableFromHashable = (hash: HashableCanvasSelectable): CanvasSelectable => {
  const barIndex = hash.indexOf('|');
  const [selectableType, value] = [hash.slice(0, barIndex), hash.slice(barIndex + 1)];
  switch (selectableType) {
    case RenderedCanvasNodeType.Node:
      return nodeIdToSelectable(nodeIdFromHashable(value as HashableNodeId));
    case RenderedCanvasNodeType.CollapsedPairwiseNode:
      return collapsedPairwiseNodeIdToSelectable(value);
    case RenderedCanvasNodeType.CollapsedGroup:
      return collapsedGroupIdToSelectable(value);
    default:
      throw Error(`unable to parsed selectable hashable ${hash}`);
  }
};

export const extractSelectedNodeIds = (selectables: CanvasSelectable[]): NodeId[] => {
  return selectables
    .filter((s) => s.type === RenderedCanvasNodeType.Node)
    .map((s) => s.selectable as NodeId);
};

export const extractSelectedCollapsedPairwiseNodeIds = (
  selectables: CanvasSelectable[],
): CollapsedPairwiseNodeId[] => {
  return selectables
    .filter((s) => s.type === RenderedCanvasNodeType.CollapsedPairwiseNode)
    .map((s) => s.selectable as CollapsedPairwiseNodeId);
};

export const extractSelectedCollapsedGroupIds = (
  selectables: CanvasSelectable[],
): CollapsedGroupId[] => {
  return selectables
    .filter((s) => s.type === RenderedCanvasNodeType.CollapsedGroup)
    .map((s) => s.selectable as CollapsedGroupId);
};

export enum Directionality {
  Forward = 'forward',
  Backward = 'backward',
  Bidirectional = 'bidirectional',
}

export interface Edge {
  id1: NodeId;
  id2: NodeId;
  directionality: Directionality;
}

export interface Cluster {
  nodes: NodeId[];
}

export interface V2Data {
  nodes: Node[];
  edges: Edge[];
  nonfetchedEdgeCounts: Map<HashableNodeId, number>;
  seedNodes: Node[];
}

export interface CollapsedGroupPreviewData {
  v2Data: V2Data;
  nodePositions: { [nodeId: HashableNodeId]: NodePosition };
  lightHydrationData: { [nodeId: HashableNodeId]: LightlyHydratedNode };
  viewType: string;
}

// --------------------------------------------------------------------------
// Data structures for interacting with the G6 canvas for Graph V2.
// --------------------------------------------------------------------------

export type G6NodeId = string;
export type G6EdgeId = string;

export interface G6Node extends NodeConfig {
  dataNode: Node;
}

export interface G6CollapsedGroup extends NodeConfig {
  dataNode: CollapsedGroup;
}

export interface G6CollapsedPairwiseNode extends NodeConfig {
  dataNode: CollapsedPairwiseNode;
}

export interface G6Edge extends EdgeConfig {}

export interface G6AddNodeMessage {
  messageType: 'add_node';
  node: G6Node;
}

export interface G6AddCollapsedGroupMessage {
  messageType: 'add_collapsed_group';
  collapsedGroup: G6CollapsedGroup;
}

export interface G6AddCollapsedPairwiseNodeMessage {
  messageType: 'add_collapsed_pairwise_node';
  node: G6CollapsedPairwiseNode;
}

export interface G6UpdateCollapsedPairwiseNodeMessage {
  messageType: 'update_collapsed_pairwise_node';
  node: G6CollapsedPairwiseNode;
}

export interface G6RemoveCollapsedPairwiseNodeMessage {
  messageType: 'remove_collapsed_pairwise_node';
  node: G6CollapsedPairwiseNode;
}

export interface G6UpdateCollapsedGroupMessage {
  messageType: 'update_collapsed_group';
  collapsedGroup: G6CollapsedGroup;
}

export interface G6UpdateNodeMessage {
  messageType: 'update_node';
  node: G6Node;
}

export interface G6RemoveNodeMessage {
  messageType: 'remove_node';
  id: G6NodeId;
}

export interface G6RemoveCollapsedGroupMessage {
  messageType: 'remove_collapsed_group';
  id: G6NodeId;
}

export interface G6AddEdgeMessage {
  messageType: 'add_edge';
  edge: G6Edge;
}

export interface G6UpdateEdgeMessage {
  messageType: 'update_edge';
  edge: G6Edge;
}

export interface G6RemoveEdgeMessage {
  messageType: 'remove_edge';
  edgeId: G6EdgeId;
}

export type G6GraphUpdateItem =
  | G6AddNodeMessage
  | G6UpdateNodeMessage
  | G6RemoveNodeMessage
  | G6AddEdgeMessage
  | G6UpdateEdgeMessage
  | G6RemoveEdgeMessage
  | G6AddCollapsedGroupMessage
  | G6AddCollapsedPairwiseNodeMessage
  | G6RemoveCollapsedPairwiseNodeMessage
  | G6UpdateCollapsedPairwiseNodeMessage
  | G6UpdateCollapsedGroupMessage
  | G6RemoveCollapsedGroupMessage;

// The interface to pass back to the G6Wrapper to make incremental updates to the canvas.
export interface G6GraphUpdateMessage {
  items: G6GraphUpdateItem[];
}

export const newEmptyGraphUpdateMessage = (): G6GraphUpdateMessage => {
  return {
    items: [],
  };
};

export const combineGraphUpdateMessages = (
  messages: G6GraphUpdateMessage[],
): G6GraphUpdateMessage => {
  const combinedMessage: G6GraphUpdateMessage = newEmptyGraphUpdateMessage();
  messages.forEach((message) => {
    combinedMessage.items.push(...message.items);
  });
  return combinedMessage;
};

// The interface to pass back to the G6Wrapper to override and rerender the nodes/edges on the canvas.
export interface G6SetGraphMessage {
  nodes: G6Node[];
  edges: G6Edge[];
}

// --------------------------------------------------------------------------
// Hover card
// --------------------------------------------------------------------------
export interface HoverCardState {
  canvasX: number;
  canvasY: number;
  hovered: CanvasSelectable;
}
